/* T Squared custom putter configurator (v2).
 *
 * Built from the HPE x Commvault event configurator: every picture is a real
 * studio photo of the putter with the website's own color layers stacked on
 * top (one sprite per zone, see tscfg-assets.json).
 *
 * Shopify is the source of truth for products, prices and lie variants. Every
 * other choice travels as a line item property, using the exact names the old
 * configurator sent, because scripts/build_sheet_mailer.py builds Scott's build
 * sheet from them. Rename a property here and the build sheet loses that row.
 *
 * The CORE block below has no DOM access so it can be tested in Node
 * (outputs/configurator-v2/tests).
 */
(function (root) {
  "use strict";

  // ------------------------------------------------------------------ core
  const CORE = (() => {
    const TEN = ["Black", "Blue", "Pink", "Gold", "Orange", "Grey", "Brown", "Green", "Purple", "Red"];
    const SIGHT = ["White", ...TEN];
    const SWATCH = { Black: "#1c1c1e", Blue: "#1f4fa8", Pink: "#e0568c", Gold: "#b8903c", Orange: "#e0761f",
      Grey: "#7d7d80", Brown: "#6b4423", Green: "#1f7a3d", Purple: "#6b3fa0", Red: "#d81e26", White: "#f2f2f2",
      Silver: "#c9ccd1" };
    // Red Standard is out of stock (2026-06-02). Names match what orders carry today.
    const GRIPS = ["Black Standard", "Black Pistol Oversize", "Red Pistol Oversize"];

    const FLAT_CAT = ["Flat Cat Fat Original", "Flat Cat Original Svelte", "Flat Cat Pistol", "Flat Cat Solution Pistol (Weighted)",
      "Flat Cat Standard Original", "Flat Cat Standard Solution (Weighted)", "Flat Cat Standard Tak", "Flat Cat Svelte Tak"];
    const LENGTHS = ["33", "33.5", "34", "34.5", "35", "35.5", "36", "36.5"];
    const BLADE_COVERS = ["Standard Blade/Mid Mallet", "Standard Blade/Mid Mallet (White)", "Pink Ribbon"];   // Black Buffalo dropped 2026-10-12
    const MALLET_COVERS = ["Standard Mallet", "Buffalo Plaid (white)"];
    const VIEW_LABEL = { sole: "Sole", address: "Top", back: "Back" };
    const LOFT = "4° (Standard)";
    // Mallet lies by neck (Mike, 2026-10-12): a single bend only 1° either way from 70°;
    // the 1104 flow neck and the 1106+ centre shaft 68° to 72°.
    const MALLET_LIES = (n) => (n === "Bend" ? [69, 70, 71] : [68, 69, 70, 71, 72]);
    const LIE_TITLES = ["67° (3° Flat)", "68° (2° Flat)", "69° (1° Flat)", "70° Standard", "71° (1° Upright)", "72° (2° Upright)"];
    // Milled face models (503, 713, 905, 912) take a loft choice; every other model is fixed 4° (Mike, 2026-10-12).
    const LOFTS = ["2°", "3°", LOFT];

    const z = (key, label, prop, proc, view, extra) => Object.assign({ key, label, prop, proc, view, colors: TEN }, extra);
    const HOSEL = z("hosel", "Hosel", "Hosel", "Anodized", "back", { views: ["sole", "back", "address"] });
    const BACK = z("back_plate", "Back Plate", "Back Plate", "Anodized", "back");
    // TS-713i: the colored part on the back is an epoxy T fill, not an anodized plate (Mike,
    // 2026-10-13). Same art (zone key back_plate); prints on the sheet's BACK T EPOXY row.
    const BACK_T = z("back_plate", "Back T Fill", "Back T", "Epoxy", "back");
    const SIGHTLINE = z("sightline", "Sightline", "Sightline", "Painted", "address", { colors: SIGHT });
    // Paint fill in the engraved model number on the back of the blades and mid mallets (Mike, 2026-10-22).
    // Silver heads only: on a black finish the art missed the engraving and showed white specks, so
    // "if they select black finish then no paint for model number" (Mike). Switching to Black drops it.
    const MODEL_FILL = z("model_fill", "Model Number Fill", "Model Number Fill", "Painted", "back",
      { colors: SIGHT, only: (c) => c.finish !== "Black" });
    const BOTTOM_T = z("bottom_t", "Bottom T Fill", "Bottom T", "Epoxy", "sole");
    const FACE_T = z("face_t", "Face T Fill", "Face T", "Epoxy", "sole");
    const FACE = z("face_color", "Face Color", "Face", "Anodized", "sole");
    // On the mallets the base plate is also the dark part seen from the top (Mike, 2026-10-12).
    const BASE = z("base_plate", "Base Plate", "Base Plate", "Anodized", "sole", { views: ["sole", "address"] });
    const INLAY = z("sole_inlay", "Sole Inlay", "Sole Inlay", "Anodized", "sole");

    // Plumber's neck finish (blades and mid mallets). Reported under the neck's own
    // name ("Short Plumber Neck": "Black"), as the old configurator did.
    const NECK_FINISH = z("neck_finish", "Neck Finish", null, null, "back", { colors: ["Silver", "Black"], views: ["sole", "back"], step: 1, noStock: true });
    const PLUMBER = [
      { label: "Toe Hang", sub: "Short plumber's neck", value: "Short Plumbers Neck", neck: "Short" },
      { label: "Face Balanced", sub: "Long plumber's neck", value: "Long Plumbers Neck", neck: "Long" },
    ];
    const blade = (o) => { o.zones = [NECK_FINISH].concat(o.zones); return Object.assign({
      finishes: ["Silver", "Black"], views: ["sole", "address", "back"], balances: PLUMBER, balanceLabel: "Neck",
      covers: () => BLADE_COVERS, lie: true, loft: true, weights: null,
    }, o); };

    const MODELS = {
      "503": blade({ lofts: LOFTS, handle: "ts-503-blade-custom", name: "TS-503", type: "Blade", line: "Classic blade. Diamond milled face.",
        zones: [HOSEL, BACK, SIGHTLINE, BOTTOM_T, FACE_T, MODEL_FILL] }),
      "713": blade({ lofts: LOFTS, handle: "ts-713-mid-mallet-custom", name: "TS-713", type: "Mid Mallet", line: "Diamond milled face. Sits square.",
        zones: [HOSEL, BACK, SIGHTLINE, BOTTOM_T, FACE_T, MODEL_FILL] }),
      "713i": blade({ handle: "ts-713i-mid-mallet-custom", name: "TS-713i", type: "Mid Mallet", line: "Soft Core face insert in your color.",
        zones: [HOSEL, FACE, BACK_T, SIGHTLINE, BOTTOM_T, MODEL_FILL] }),
      "905": blade({ lofts: LOFTS, handle: "ts-905-mid-mallet-custom", name: "TS-905", type: "Mid Mallet", line: "Compact mid mallet. Diamond milled face.",
        zones: [HOSEL, BACK, SIGHTLINE, FACE_T, MODEL_FILL] }),
      "912": blade({ lofts: LOFTS, handle: "ts-912-mid-mallet-tour-custom", name: "TS-912", type: "Mid Mallet", line: "Tour mid mallet. Diamond milled face.",
        zones: [HOSEL, BACK, SIGHTLINE, FACE_T, MODEL_FILL] }),
      "1029": { handle: "ts-1029-mallet-custom", name: "TS-1029", type: "Mallet", line: "Two piece aluminum. The whole head takes color.",
        finishes: TEN, finishName: (f) => (f === "Grey" ? "Gunmetal Grey" : f), previewFinish: "Grey", views: ["sole", "address"],
        balanceLabel: "Shaft position", balances: [
          { label: "Heel Shaft", sub: "Face balanced", value: "Plumbers neck", neck: "Heel" },
          { label: "Center Shaft", sub: "Face balanced", value: "Center Shaft", neck: "Center" }],
        // The base plate is the same part as the top insert around the sightline, so it shows in both views (Mike, 2026-10-12).
        zones: [BASE, FACE_T,
          Object.assign({}, HOSEL, { view: "sole", views: ["sole", "address"], only: (c, n) => n === "Heel" }), SIGHTLINE],
        covers: (n) => (n === "Center" ? ["Standard Mallet"] : MALLET_COVERS), lie: true, loft: false,
        weights: ["350g (Light)", "372g (Heavy)", "394g (Heaviest)"], legacy: true },
      "1104": { handle: "ts-1104-mallet-custom", name: "TS-1104", type: "Mallet", line: "Soft Core mallet. Flow neck or single bend.",
        finishes: ["Silver", "Black"], views: ["sole", "address"], balanceLabel: "Neck", balances: [
          { label: "Toe Hang", sub: "Flow neck", value: "Flow Neck", neck: "Flow" },
          { label: "Face Balanced", sub: "Single bend", value: "Single Bend", neck: "Bend" }],
        zones: [FACE, BASE, SIGHTLINE, z("neck", "Neck", null, "Anodized", "sole", { views: ["sole", "address"] })],
        // No lie variants in Shopify for the 1104, so its lie is a property only (lieTitles).
        covers: () => MALLET_COVERS, lie: false, lieTitles: LIE_TITLES, lieRange: MALLET_LIES, loft: false, weights: null },
      "1106": { handle: "ts-1106-mallet-face-balanced-only-custom", name: "TS-1106+", type: "Mallet", line: "Soft Core mallet. Single bend or center shaft.",
        finishes: ["Silver", "Black"], views: ["sole", "address"], balanceLabel: "Neck", balances: [
          { label: "Single Bend", sub: "Face balanced", value: "Single Bend", neck: "Bend" },
          { label: "Center Shaft", sub: "Face balanced", value: "Center Shaft", neck: "Center" }],
        zones: [FACE, BASE, SIGHTLINE, INLAY, z("neck", "Neck", null, "Anodized", "sole", { views: ["sole", "address"], only: (c, n) => n !== "Center" })],
        covers: (n) => (n === "Center" ? ["Standard Mallet"] : MALLET_COVERS), lie: true, lieRange: MALLET_LIES, loft: false, weights: null },
    };
    const ORDER = ["503", "713", "713i", "905", "912", "1029", "1104", "1106"];
    for (const k of ORDER) {
      MODELS[k].key = k;
      MODELS[k].finishName = MODELS[k].finishName || ((f) => f);
      MODELS[k].previewFinish = MODELS[k].previewFinish || "Silver";
    }
    const byHandle = (h) => ORDER.find((k) => MODELS[k].handle === h) || null;

    function freshCfg() {
      return { finish: null, balance: null, shaft: null, zones: {}, grip: null, cover: null, hand: null, length: null, lie: null, loft: null, weight: null };
    }
    const balanceOf = (m, c) => m.balances.find((b) => b.value === c.balance) || null;
    const neckOf = (m, c) => (balanceOf(m, c) || m.balances[0]).neck;
    const zonesFor = (m, c) => m.zones.filter((zz) => !zz.only || zz.only(c, neckOf(m, c)));
    const coversFor = (m, c) => m.covers(neckOf(m, c));
    const inRange = (m, c, title) => { const r = m.lieRange && m.lieRange(neckOf(m, c)); return !r || r.includes(parseInt(title, 10)); };
    // Every lie this model and neck can take: Shopify variants where they exist, else the 1104's list.
    function liesFor(m, c, product) {
      const titles = m.lie ? lieVariants(product).map((v) => v.title) : (m.lieTitles || []);
      return titles.filter((t) => inRange(m, c, t));
    }
    // A neck color is reported under the neck's own name, as the old configurator did.
    const zoneProp = (m, c, zz) => {
      if (zz.prop) return zz.prop;
      const b = balanceOf(m, c) || {};
      if (zz.key === "neck_finish") return `${b.neck} Plumber Neck`;
      return b.value || "Neck";
    };

    // Lie angles are Shopify variants. "None" is a leftover placeholder variant, never offered.
    function lieVariants(product) {
      if (!product) return [];
      return product.variants.filter((v) => v.title !== "None" && v.title !== "Default Title");
    }
    function variantFor(m, c, product) {
      if (!product) return null;
      const lies = lieVariants(product);
      if (m.lie && lies.length) return lies.find((v) => v.title === c.lie) || null;
      return product.variants.find((v) => v.title === "Default Title") || product.variants[0] || null;
    }

    // Keep choices consistent after a change: a centre shaft has no neck color
    // and only takes the Standard Mallet cover. A cover with one option is not a choice.
    function normalise(m, c) {
      const live = new Set(zonesFor(m, c).map((zz) => zz.key));
      for (const k of Object.keys(c.zones)) if (!live.has(k)) delete c.zones[k];
      // No "Stock" color any more (Mike, 2026-10-13): a build saved with it asks again.
      for (const k of Object.keys(c.zones)) if (c.zones[k] === "Stock") delete c.zones[k];
      const covers = coversFor(m, c);
      if (c.cover && !covers.includes(c.cover)) c.cover = null;
      if (covers.length === 1 && c.balance) c.cover = covers[0];
      if (c.lie && !inRange(m, c, c.lie)) c.lie = null;
      return c;
    }

    // Everything a customer must choose, grouped by step. Nothing is pre-selected:
    // the $599 is for a putter the customer actually specified (Mike, 2026-05-14).
    function required(m, c, product) {
      const out = [];
      const need = (step, label, ok) => { if (!ok) out.push({ step, label }); };
      need(1, "Head finish", c.finish);
      need(1, m.balanceLabel, c.balance);
      need(1, "Shaft", c.shaft);
      for (const zz of zonesFor(m, c)) need(zz.step || 2, zz.label, c.zones[zz.key]);
      need(3, "Grip", c.grip);
      need(3, "Head cover", c.cover);
      need(4, "Hand", c.hand);
      need(4, "Length", c.length);
      if (liesFor(m, c, product).length) need(4, "Lie", c.lie);
      if (m.lofts) need(4, "Loft", c.loft);
      if (m.weights) need(4, "Head weight", c.weight);
      return out;
    }

    function properties(m, c, imageUrl) {
      const p = {};
      if (imageUrl) p.Image = imageUrl;
      p.Finish = m.finishName(c.finish);
      p.Shaft = c.shaft;
      p.Balance = c.balance;
      for (const zz of zonesFor(m, c)) p[zoneProp(m, c, zz)] = c.zones[zz.key];
      if (c.lie) p["Club Lie"] = c.lie;
      if (m.lofts) p["Club Loft"] = c.loft;
      else if (m.loft) p["Club Loft"] = LOFT;
      p["Hand Style"] = c.hand === "Left" ? "Left hand" : "Right hand";
      p["Grip Style"] = c.grip;
      p["Shaft Length"] = c.length;
      p.Headcover = c.cover;
      if (m.weights) p["Head Weight"] = c.weight;
      p._Configurator = "v2";
      return p;
    }

    function summary(m, c) {
      const rows = [["Model", `${m.name} ${m.type}`], ["Head finish", c.finish && m.finishName(c.finish)]];
      const b = balanceOf(m, c);
      rows.push([m.balanceLabel, b && `${b.label} (${b.sub})`], ["Shaft", c.shaft]);
      for (const zz of zonesFor(m, c)) rows.push([zz.label, c.zones[zz.key]]);
      rows.push(["Grip", c.grip], ["Head cover", c.cover], ["Hand", c.hand], ["Length", c.length && `${c.length}"`]);
      if (m.lie || m.lieTitles) rows.push(["Lie", c.lie]);
      rows.push(["Loft", m.lofts ? c.loft : "4°"]);
      if (m.weights) rows.push(["Head weight", c.weight]);
      return rows;
    }

    // One stack of real photo layers: the base photo, then each recolored zone.
    // Anything not chosen yet is drawn as the stock build.
    // What an unchosen part looks like, so every model previews the same stock build (Mike,
    // 2026-10-13): the plumber's neck is silver until a neck finish is picked, and on a black
    // head the T fills are grey, as the black TS-713 photo shows. Preview only; never ordered.
    function stockLook(zz, f) {
      if (zz.key === "neck_finish") return "Silver";
      if (f === "Black" && zz.proc === "Epoxy") return "Grey";              // bottom, face and back T fills
      return null;
    }

    function layersFor(m, c, view, A) {
      const a = A[m.key] || {};
      const out = [];
      const add = (k) => { if (a[k]) out.push(a[k]); };
      const n = neckOf(m, c);
      const f = c.finish || m.previewFinish;
      if (m.legacy) {                                   // TS-1029: one photo per anodize color
        add(`base/${f}/${view}/${n}`);
        const zc = (k) => c.zones[k];
        const plate = zc("base_plate");
        if (plate && plate !== "Stock") add(`base_plate/${view}/${n}/${plate}`);       // top view: the insert (Black is the photo)
        if (view === "sole" && zc("face_t") && zc("face_t") !== "Stock") add(`face_t/sole/${n}/${zc("face_t")}`);
        if (n === "Heel" && zc("hosel") && zc("hosel") !== "Stock") add(`hosel/${view}/Heel/${zc("hosel")}`);
        const s = zc("sightline");
        if (view === "address" && s && s !== "Stock" && s !== "White") add(`sightline/address/${n}/${s}`);
        if (c.shaft === "Silver") add(`shaft/${view}/${n}/Silver`);
        return out;
      }
      const pick = (k) => (a[`${k}/${f}`] !== undefined ? `${k}/${f}` : k);    // finish specific art first
      add(`base/${n}/${view}`);
      if (f === "Black") add(`finish/${n}/${view}`);
      const native = typeof a.native_shaft === "object" ? a.native_shaft[f] : a.native_shaft;
      if (c.shaft && c.shaft !== native) add(pick(`shaft/${n}/${view}/${c.shaft}`));
      for (const zz of zonesFor(m, c)) {
        const col = c.zones[zz.key] || stockLook(zz, f), sp = a[pick(`zone/${zz.key}/${n}/${view}`)];
        if (!sp || !col || col === "Stock") continue;
        const i = sp.colors.indexOf(col);
        if (i >= 0) out.push({ url: sp.sprite, box: sp.box, i, n: sp.colors.length });
      }
      return out;
    }

    const zoneViews = (zz) => zz.views || [zz.view];

    return { TEN, SIGHT, SWATCH, GRIPS, LOFTS, FLAT_CAT, LENGTHS, VIEW_LABEL, MODELS, ORDER, byHandle,
      freshCfg, balanceOf, neckOf, zonesFor, coversFor, lieVariants, variantFor, normalise, required,
      properties, summary, layersFor, zoneViews, liesFor, LIE_TITLES };
  })();

  if (typeof module !== "undefined" && module.exports) { module.exports = CORE; return; }
  root.TSCFG = CORE;

  // ------------------------------------------------------------------ browser
  const CLOUD = { name: "dl2scmfzl", preset: "combined image" };   // same account the old configurator used
  const STEPS = ["Model", "Head", "Color", "Grip", "Fit", "Review"];
  const F = 1200;                                                   // every layer is authored on a 1200px frame

  function boot(el) {
    const data = JSON.parse(el.querySelector("[data-tscfg-data]").textContent);
    // Image names carry a content hash (export_assets.py), which is what keeps the CDN from
    // serving an old picture; the stamp alone did not (Shopify caches converted AVIF copies).
    const [assetPath, stamp] = data.assetsUrl.split("?");
    const base = assetPath.slice(0, assetPath.lastIndexOf("/") + 1);
    const url = (file) => base + file + (stamp ? "?" + stamp : "");
    const products = {};
    for (const p of data.products) if (p) products[p.handle] = p;
    const money = (cents) => {
      try { return new Intl.NumberFormat(undefined, { style: "currency", currency: data.currency || "USD", minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100); }
      catch (e) { return "$" + (cents / 100).toFixed(2); }
    };

    const $ = (s) => el.querySelector(s);
    const h = (tag, props, kids) => {
      const e = document.createElement(tag);
      for (const [k, v] of Object.entries(props || {})) {
        if (v == null || v === false) continue;
        if (k === "on") e.addEventListener("click", v);
        else if (k === "text") e.textContent = v;
        else if (k === "cls") e.className = v;
        else e.setAttribute(k, v === true ? "" : v);
      }
      for (const kid of [].concat(kids || [])) if (kid != null && kid !== false) e.append(kid);
      return e;
    };

    let A = {};
    const S = { step: 1, model: CORE.byHandle(data.current) || "905", view: "sole", cfg: null, busy: false, error: "", lastZone: null };
    const M = () => CORE.MODELS[S.model];
    const P = () => products[M().handle];
    const store = (k) => "tscfg:" + CORE.MODELS[k].handle;
    function load(k) {
      try { const s = JSON.parse(sessionStorage.getItem(store(k)) || "null"); if (s && s.zones) return Object.assign(CORE.freshCfg(), s); } catch (e) {}
      return CORE.freshCfg();
    }
    function save() { try { sessionStorage.setItem(store(S.model), JSON.stringify(S.cfg)); } catch (e) {} }

    // ---------------------------------------------------------------- painting
    const loaded = new Map();
    function preload(list) {
      return Promise.all(list.map((s) => (typeof s === "string" ? s : s.url)).map((f) => {
        if (!loaded.has(f)) loaded.set(f, new Promise((res) => { const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = url(f); }));
        return loaded.get(f);
      }));
    }
    function layerEl(s) {
      if (typeof s === "string") return h("img", { src: url(s), alt: "", draggable: "false" });
      const [x, y, w, hh] = s.box;
      const d = h("div", { cls: "tc-sp" });
      Object.assign(d.style, { left: (x / F) * 100 + "%", top: (y / F) * 100 + "%", width: (w / F) * 100 + "%", height: (hh / F) * 100 + "%",
        backgroundImage: `url("${url(s.url)}")`, backgroundSize: `100% ${s.n * 100}%`,
        backgroundPosition: `0 ${s.n > 1 ? (s.i / (s.n - 1)) * 100 : 0}%` });
      return d;
    }
    let seq = 0;
    async function paint(frame, list) {
      const mine = ++seq;
      frame.classList.add("is-loading");
      await preload(list);
      if (mine !== seq) return;                         // a newer change already won
      frame.classList.remove("is-loading");
      const next = h("div", { cls: "tc-layers" });
      next.style.opacity = "0";
      for (const s of list) next.append(layerEl(s));
      frame.append(next);
      requestAnimationFrame(() => requestAnimationFrame(() => { next.style.opacity = "1"; }));
      const old = [...frame.querySelectorAll(".tc-layers")].slice(0, -1);
      setTimeout(() => old.forEach((o) => o.remove()), 360);
    }

    // The build image Scott sees on the order: the sole view, composited on a canvas.
    async function composite() {
      const list = CORE.layersFor(M(), S.cfg, M().views[0], A);
      const imgs = await Promise.all(list.map((s) => new Promise((res) => {
        const i = new Image(); i.crossOrigin = "anonymous";
        i.onload = () => res(i); i.onerror = () => res(null); i.src = url(typeof s === "string" ? s : s.url);
      })));
      if (imgs.some((i) => !i)) return null;
      const W = 1000, cv = document.createElement("canvas"); cv.width = cv.height = W;
      const g = cv.getContext("2d");
      g.fillStyle = "#ffffff"; g.fillRect(0, 0, W, W);
      list.forEach((s, k) => {
        const im = imgs[k];
        if (typeof s === "string") { g.drawImage(im, 0, 0, W, W); return; }
        const [x, y, w, hh] = s.box, row = im.naturalHeight / s.n;
        g.drawImage(im, 0, s.i * row, im.naturalWidth, row, (x / F) * W, (y / F) * W, (w / F) * W, (hh / F) * W);
      });
      return cv.toDataURL("image/jpeg", 0.88);
    }
    async function uploadImage() {
      try {
        const dataUrl = await composite();
        if (!dataUrl) return null;
        const ctl = new AbortController(), t = setTimeout(() => ctl.abort(), 12000);
        const fd = new FormData(); fd.append("file", dataUrl); fd.append("upload_preset", CLOUD.preset);
        const r = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD.name}/image/upload`, { method: "POST", body: fd, signal: ctl.signal });
        clearTimeout(t);
        if (!r.ok) return null;
        return (await r.json()).secure_url || null;
      } catch (e) { return null; }                      // the picture is a courtesy; the order never waits on it
    }

    // ---------------------------------------------------------------- stage
    function renderStage() {
      const m = M();
      $("[data-name]").textContent = m.name;
      $("[data-type]").textContent = `${m.type} · Custom`;
      if (!m.views.includes(S.view)) S.view = m.views[0];
      const v = $("[data-views]"); v.innerHTML = "";
      for (const view of m.views) v.append(h("button", { type: "button", "aria-pressed": String(view === S.view), text: CORE.VIEW_LABEL[view],
        on: () => { S.view = view; renderStage(); } }));
      const fr = $("[data-frame]"); fr.dataset.model = S.model; fr.dataset.view = S.view;   // lets CSS frame a view per model
      paint(fr, CORE.layersFor(m, S.cfg, S.view, A));
      const hint = $("[data-hint]");
      const zz = S.lastZone && CORE.zonesFor(m, S.cfg).find((x) => x.key === S.lastZone);
      const notes = [];
      if (zz && !CORE.zoneViews(zz).includes(S.view)) notes.push(`${zz.label} shows in the ${CORE.VIEW_LABEL[zz.view]} view`);
      else if (!S.cfg.finish) notes.push("Showing the stock build until you choose");
      if (S.cfg.hand === "Left") notes.push("Pictured right handed. Yours is built left handed.");
      hint.textContent = notes.join(" · ");
      hint.hidden = !notes.length;
    }

    // ---------------------------------------------------------------- rail
    function group(label, value, proc, body, missing) {
      return h("div", { cls: "tc-group" + (missing ? " is-missing" : "") }, [
        h("div", { cls: "tc-group-head" }, [h("span", { cls: "tc-label", text: label }),
          h("span", { cls: "tc-val" }, [value || (value === "" ? "" : h("em", { text: "Choose" })), proc ? h("span", { cls: "tc-proc", text: proc }) : null])]),
        body]);
    }
    const chips = (opts, cur, pick, labelOf, disabled) => h("div", { cls: "tc-chips" }, opts.map((o) => h("button", {
      type: "button", cls: "tc-chip", "aria-pressed": String(o === cur), disabled: disabled && disabled(o),
      on: () => pick(o) }, labelOf ? labelOf(o) : o)));
    const swatches = (names, cur, pick, stock) => h("div", { cls: "tc-swatches" }, (stock ? ["Stock"].concat(names) : names).map((n) => h("button", {
      type: "button", cls: "tc-sw" + (n === "Stock" ? " is-stock" : ""), title: n, "aria-label": n, "aria-pressed": String(n === cur),
      style: n === "Stock" ? null : `background:${CORE.SWATCH[n]}`, on: () => pick(n) })));
    const update = (fn) => { fn(S.cfg); CORE.normalise(M(), S.cfg); S.error = ""; save(); renderAll(); };

    function renderRail() {
      const m = M(), c = S.cfg, body = $("[data-rail]");
      // Keep the shopper's place while they pick within a step; only a new step starts at the top
      // (Mike, 2026-10-13: picking a sole inlay color bounced the panel back to the top options).
      const sameStep = body.dataset.step === String(S.step) && body.dataset.model === S.model;
      const keepRail = body.scrollTop, keepPage = window.scrollY;
      body.dataset.step = String(S.step); body.dataset.model = S.model;
      body.innerHTML = "";
      const title = (t, s) => h("div", { cls: "tc-title" }, [h("h2", { text: t }), s ? h("p", { text: s }) : null]);
      const miss = S.showMissing ? new Set(CORE.required(m, c, P()).map((r) => r.label)) : new Set();

      if (S.step === 0) {
        body.append(title("Choose your putter", "Eight shapes, each milled in Orchard Park, New York."));
        body.append(h("div", { cls: "tc-models" }, CORE.ORDER.filter((k) => products[CORE.MODELS[k].handle]).map((k) => {
          const mm = CORE.MODELS[k], first = CORE.layersFor(mm, CORE.freshCfg(), "sole", A)[0];
          return h("button", { type: "button", cls: "tc-mcard", "aria-pressed": String(k === S.model), on: () => switchModel(k) }, [
            first ? h("img", { src: url(first), alt: "", loading: "lazy" }) : h("span"),
            h("span", {}, [h("b", { text: mm.name }), h("span", { cls: "tc-t", text: mm.type }), h("span", { cls: "tc-d", text: mm.line })])]);
        })));
      }
      if (S.step === 1) {
        body.append(title("Head", m.legacy ? "The whole head is anodized in the color you choose." : "Silver or black head, then the neck and shaft."));
        body.append(group("Head finish", c.finish && m.finishName(c.finish), m.legacy ? "Anodized" : null,
          m.legacy ? swatches(m.finishes, c.finish, (f) => update((x) => { x.finish = f; }))
            : chips(m.finishes, c.finish, (f) => update((x) => { x.finish = f; })), miss.has("Head finish")));
        const b = CORE.balanceOf(m, c);
        body.append(group(m.balanceLabel, b && b.label, null, h("div", { cls: "tc-chips" }, m.balances.map((o) => h("button", {
          type: "button", cls: "tc-chip tc-chip-2", "aria-pressed": String(o.value === c.balance), on: () => update((x) => { x.balance = o.value; }) },
          [h("span", { text: o.label }), h("small", { text: o.sub })]))), miss.has(m.balanceLabel)));
        for (const zz of CORE.zonesFor(m, c).filter((x) => x.step === 1)) {
          body.append(group(zz.label, c.zones[zz.key], null, chips(zz.colors, c.zones[zz.key], (col) => {
            S.lastZone = zz.key;
            if (!CORE.zoneViews(zz).includes(S.view)) S.view = zz.view;
            update((x) => { x.zones[zz.key] = col; });
          }), miss.has(zz.label)));
        }
        body.append(group("Shaft", c.shaft, null, chips(["Black", "Silver"], c.shaft, (s) => update((x) => { x.shaft = s; })), miss.has("Shaft")));
      }
      if (S.step === 2) {
        body.append(title("Color", "Pick a color for each part."));
        for (const zz of CORE.zonesFor(m, c).filter((x) => !x.step)) {
          body.append(group(zz.label, c.zones[zz.key], zz.proc, swatches(zz.colors, c.zones[zz.key], (col) => {
            S.lastZone = zz.key;
            if (!CORE.zoneViews(zz).includes(S.view)) S.view = zz.view;
            update((x) => { x.zones[zz.key] = col; });
          }, false), miss.has(zz.label)));
        }
        body.append(h("p", { cls: "tc-note", text: "Shown on studio photography of the real putter. Anodized parts carry a metallic sheen in person." }));
      }
      if (S.step === 3) {
        body.append(title("Grip and cover"));
        const tile = (name, cur, pick, photo, label, bg) => h("button", {
          type: "button", cls: "tc-grip", "aria-pressed": String(name === cur), on: () => pick(name) },
          [photo ? h("img", { src: url(photo), alt: "", loading: "lazy", style: bg ? `background:${bg}` : null }) : null, h("span", { text: label || name })]);
        const gp = (g) => A.grips && A.grips[g];
        const pickGrip = (g) => update((x) => { x.grip = g; });
        body.append(group("T Squared grips", CORE.GRIPS.includes(c.grip) ? c.grip : (c.grip ? "" : null), null,
          h("div", { cls: "tc-grips" }, CORE.GRIPS.map((g) => tile(g, c.grip, pickGrip, gp(g)))), miss.has("Grip")));
        body.append(group("Flat Cat grips", CORE.FLAT_CAT.includes(c.grip) ? c.grip : "", null,
          h("div", { cls: "tc-grips" }, CORE.FLAT_CAT.map((g) => tile(g, c.grip, pickGrip, gp(g), g.replace(/^Flat Cat /, ""))))));
        const covers = CORE.coversFor(m, c);
        const cp = (v) => (A.covers && A.covers[v]) || {};
        body.append(group("Head cover", c.cover, covers.length === 1 && c.balance ? "Fits this shaft position" : null,
          h("div", { cls: "tc-grips tc-covers" }, covers.map((v) => tile(v, c.cover, (x0) => update((x) => { x.cover = x0; }), cp(v).file, null, cp(v).bg))),
          miss.has("Head cover")));
      }
      if (S.step === 4) {
        body.append(title("Fit", m.lofts ? null : "Built at 4° loft."));
        body.append(group("Hand", c.hand, null, chips(["Right", "Left"], c.hand, (v) => update((x) => { x.hand = v; })), miss.has("Hand")));
        body.append(group("Length", c.length && `${c.length}"`, null, chips(CORE.LENGTHS, c.length, (v) => update((x) => { x.length = v; }), (o) => `${o}"`), miss.has("Length")));
        const lies = CORE.liesFor(m, c, P()), variants = CORE.lieVariants(P());
        const single = m.lieRange && CORE.neckOf(m, c) === "Bend";
        if (lies.length) body.append(group("Lie", c.lie, single ? "Single bend: 1° either way" : null,
          chips(lies, c.lie, (v) => update((x) => { x.lie = v; }), null,
            (t) => { const v = variants.find((x) => x.title === t); return !!v && !v.available; }), miss.has("Lie")));
        if (m.lofts) body.append(group("Loft", c.loft, null, chips(m.lofts, c.loft, (v) => update((x) => { x.loft = v; })), miss.has("Loft")));
        if (m.weights) body.append(group("Head weight", c.weight, null, chips(m.weights, c.weight, (v) => update((x) => { x.weight = v; })), miss.has("Head weight")));
      }
      if (S.step === 5) renderReview(body);
      if (sameStep) {
        body.scrollTop = keepRail;
        if (window.scrollY !== keepPage) window.scrollTo(window.scrollX, keepPage);
      } else body.scrollTop = 0;
    }

    function renderReview(body) {
      const m = M(), c = S.cfg, product = P(), v = CORE.variantFor(m, c, product) || (product && product.variants[0]);
      const missing = CORE.required(m, c, product);
      body.append(h("div", { cls: "tc-title" }, [h("h2", { text: `Your ${m.name}` }), h("p", { text: "Check it over. Tap any line to change it." })]));
      const stepOf = { "Neck Finish": 1, "Model": 0, "Head finish": 1, [m.balanceLabel]: 1, "Shaft": 1, "Grip": 3, "Head cover": 3, "Hand": 4, "Length": 4, "Lie": 4, "Loft": 4, "Head weight": 4 };
      body.append(h("dl", { cls: "tc-sheet" }, CORE.summary(m, c).flatMap(([k, val]) => [
        h("dt", { text: k }),
        h("dd", {}, h("button", { type: "button", cls: val ? "" : "is-missing", text: val || "Choose",
          on: () => goto(k in stepOf ? stepOf[k] : 2) }))])));
      const price = v ? money(v.price) : "";
      body.append(h("div", { cls: "tc-price" }, [h("span", { cls: "tc-label", text: "Price" }), h("b", { text: price })]));
      if (missing.length && S.showMissing) body.append(h("p", { cls: "tc-err", role: "alert", text: "Still to choose: " + missing.map((r) => r.label).join(", ") + "." }));
      if (S.error) body.append(h("p", { cls: "tc-err", role: "alert", text: S.error }));
      body.append(h("p", { cls: "tc-note" }, ["Made to order in Orchard Park, New York. Team or company logo order? ",
        h("a", { href: "/pages/corporate-gifts", text: "Talk to us about corporate builds" }), "."]));
    }

    // ---------------------------------------------------------------- footer + steps
    function renderFoot() {
      const m = M(), c = S.cfg, product = P();
      const back = $("[data-back]"), next = $("[data-next]");
      back.disabled = S.step <= 0;
      const v = CORE.variantFor(m, c, product) || (product && product.variants[0]);
      $("[data-price]").textContent = v ? money(v.price) : "";
      if (S.step < 5) { next.textContent = S.step === 4 ? "Review" : "Next"; next.disabled = false; return; }
      const sold = !product || !product.available || (CORE.variantFor(m, c, product) && !CORE.variantFor(m, c, product).available);
      next.disabled = S.busy || sold;
      next.textContent = sold ? "Not available right now" : S.busy ? "Adding" : "Add to cart";
    }
    function renderSteps() {
      const nav = $("[data-steps]"); nav.innerHTML = "";
      const todo = new Set(CORE.required(M(), S.cfg, P()).map((r) => r.step));
      STEPS.forEach((name, i) => nav.append(h("button", { type: "button", "aria-current": i === S.step ? "step" : "false",
        cls: i > 0 && i < 5 && !todo.has(i) ? "is-done" : "", on: () => goto(i) }, [h("span", { text: String(i + 1).padStart(2, "0") }), name])));
    }
    function renderAll() { renderSteps(); renderRail(); renderStage(); renderFoot(); }
    function goto(i) {
      S.step = Math.max(0, Math.min(STEPS.length - 1, i));
      S.lastZone = null;
      renderAll();
      const top = el.getBoundingClientRect().top;
      if (top < 0) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    function switchModel(k) {
      if (k === S.model) { goto(1); return; }
      S.model = k; S.cfg = load(k); S.view = "sole"; S.showMissing = false; S.error = "";
      const p = P();
      if (p && history.replaceState) { try { history.replaceState(null, "", p.url + location.search); } catch (e) {} }
      goto(1);
    }

    async function addToCart() {
      const m = M(), c = S.cfg, product = P();
      const missing = CORE.required(m, c, product);
      if (missing.length) { S.showMissing = true; S.error = ""; renderAll(); return; }
      const variant = CORE.variantFor(m, c, product);
      if (!variant) { S.error = "That lie angle is not available. Pick another."; renderAll(); return; }
      S.busy = true; S.error = ""; renderFoot();
      const image = await uploadImage();
      const props = CORE.properties(m, c, image);
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        const r = await fetch(root + "cart/add.js", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties: props }] }) });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.description || j.message || "The cart did not accept this putter.");
        try { sessionStorage.removeItem(store(S.model)); } catch (e) {}
        window.location.href = root + "cart";
      } catch (e) {
        S.busy = false; S.error = (e && e.message) || "Something went wrong adding to cart. Please try again.";
        renderAll();
      }
    }

    $("[data-back]").addEventListener("click", () => goto(S.step - 1));
    $("[data-next]").addEventListener("click", () => (S.step === 5 ? addToCart() : goto(S.step + 1)));

    S.cfg = load(S.model);
    el.classList.add("is-ready");
    fetch(data.assetsUrl).then((r) => r.json()).then((j) => { A = j; renderAll(); for (const k of CORE.ORDER) preload(CORE.layersFor(CORE.MODELS[k], CORE.freshCfg(), "sole", A).slice(0, 1)); })
      .catch(() => { $("[data-hint]").textContent = "Pictures did not load. Refresh to try again."; $("[data-hint]").hidden = false; renderAll(); });
    renderAll();
  }

  const start = () => document.querySelectorAll("[data-tscfg]:not(.is-ready)").forEach(boot);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start); else start();
})(typeof window !== "undefined" ? window : this);
